import { Avatar } from "@admin/components/atoms/Avatar";
import { Badge, BadgeProps } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import { DataTable, DataTableColumn } from "@admin/components/molecules/Table";
import { useCurrentUserGroups } from "@admin/hooks/useCurrentUserGroups";
import { CompanyLocation, getAllModules, Licenses } from "@enos-monorepo/lib";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AddNewLocationModal,
  EditLocationModal,
  EditTablePasswordModal,
  EditTableUsernameModal,
} from "./Modals";
import { DeactiveLocationModal } from "./Modals/DeactiveLocationModal";
import { LicensesModal } from "./Modals/LicensesModal";

interface ActiveLocationsProps {
  locations?: CompanyLocation[];
  companyId: string;
  isLoading?: boolean;
}

export function ActiveLocations({
  locations,
  companyId,
  isLoading,
}: ActiveLocationsProps) {
  const { t } = useTranslation();
  const { isSuperAdmin } = useCurrentUserGroups();
  const [selectedLocation, setSelectedLocation] = useState<CompanyLocation>();
  const [isEditLocationModalOpen, setIsEditLocationModalOpen] = useState(false);
  const [isAddLocationModalOpen, setIsAddLocationModalOpen] = useState(false);
  const [isEditTabletPasswordModalOpen, setEditTabletPasswordModalOpen] =
    useState(false);
  const [isEditTabletUsernameModalOpen, setEditTabletUsernameModalOpen] =
    useState(false);
  const [isDeactiveLocationModalOpen, setDeactiveLocationModalOpen] =
    useState(false);
  const [isLicensesModalOpen, setLicensesModalOpen] = useState(false);

  const { data: modules } = useQuery({
    queryKey: ["modules"],
    queryFn: getAllModules,
  });

  const columns: DataTableColumn<CompanyLocation>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("common.Name"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <Avatar alt={row.original.name} src={row.original.avatarPhoto} />
            <button className="text-sm font-medium text-gray-900 hover:text-primary-active">
              <div>{row.original.name}</div>
            </button>
          </div>
        ),
      },
      {
        accessorKey: "tabUser",
        header: t("location.Tablet Accounts"),
        cell: ({ row }) => (
          <div className="font-normal text-gray-800 ">
            <div className="flex justify-between">
              {row.original.tabUser ? (
                <>
                  <div className="flex items-center">
                    {row.original.tabUser?.userName}
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-icon btn-clear btn-primary"
                      onClick={() => {
                        setEditTabletUsernameModalOpen(true);
                      }}
                    >
                      <i className="ki-filled ki-user-edit"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-icon btn-clear btn-info"
                      onClick={() => setEditTabletPasswordModalOpen(true)}
                    >
                      <i className="ki-filled ki-password-check"></i>
                    </button>
                  </div>
                </>
              ) : (
                <div>{t("location.No tablet users")}</div>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: "licenses",
        header: t("location.Licenses"),
        cell: ({ row }) => (
          <div className="font-normal text-gray-800">
            <div className="flex gap-2">
              {row.original?.licenses?.data &&
              row.original?.licenses?.data.length > 0 ? (
                row.original?.licenses?.data.map((badge) => {
                  return (
                    <Badge
                      key={badge.id}
                      type={badge.badgeType as BadgeProps["type"]}
                    >
                      {badge.moduleName === "Food"
                        ? t("common.Food")
                        : badge.moduleName === "Shift"
                          ? t("common.Shift")
                          : badge.moduleName}
                    </Badge>
                  );
                })
              ) : (
                <div>{t("location.No licenses")}</div>
              )}
            </div>
          </div>
        ),
      },
      {
        header: t("common.Actions"),
        size: 160,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <button
              onClick={() => setIsEditLocationModalOpen(true)}
              className="btn btn-sm btn-icon btn-clear btn-primary"
            >
              <i className="ki-filled ki-notepad-edit"></i>
            </button>
            {isSuperAdmin ? (
              <button
                className="btn btn-sm btn-icon btn-clear btn-success"
                onClick={() => setLicensesModalOpen(true)}
              >
                <i className="ki-filled ki-key-square"></i>
              </button>
            ) : null}
            {isSuperAdmin && (
              <button
                className="btn btn-sm btn-icon btn-clear btn-danger"
                onClick={() => setDeactiveLocationModalOpen(true)}
              >
                <i className="ki-filled ki-trash-square"></i>
              </button>
            )}
          </div>
        ),
      },
    ],
    [isSuperAdmin, t],
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        header={
          isSuperAdmin && (
            <Button onClick={() => setIsAddLocationModalOpen(true)}>
              <i className="ki-filled ki-plus-squared"></i>
              {t("location.Add New Location")}
            </Button>
          )
        }
        data={locations}
        columns={columns}
        title={t("location.Active Locations")}
        onRowClick={setSelectedLocation}
      />

      <EditLocationModal
        companyId={companyId}
        open={isEditLocationModalOpen}
        onClose={() => setIsEditLocationModalOpen(false)}
        companyLocation={selectedLocation}
      />
      <EditTableUsernameModal
        open={isEditTabletUsernameModalOpen}
        onClose={() => setEditTabletUsernameModalOpen(false)}
        location={selectedLocation as CompanyLocation}
      />
      <EditTablePasswordModal
        open={isEditTabletPasswordModalOpen}
        onClose={() => setEditTabletPasswordModalOpen(false)}
        location={selectedLocation as CompanyLocation}
      />
      <AddNewLocationModal
        companyId={companyId}
        open={isAddLocationModalOpen}
        onClose={() => setIsAddLocationModalOpen(false)}
      />
      <DeactiveLocationModal
        open={isDeactiveLocationModalOpen}
        onClose={() => setDeactiveLocationModalOpen(false)}
        locationId={selectedLocation?.id ?? ""}
        locationName={selectedLocation?.name ?? ""}
      />
      <LicensesModal
        modules={modules ?? []}
        licenses={selectedLocation?.licenses as Licenses}
        open={isLicensesModalOpen}
        onClose={() => setLicensesModalOpen(false)}
        locationId={selectedLocation?.id ?? ""}
      />
    </>
  );
}
